import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch,
  serverTimestamp,
  documentId,
  getCountFromServer,
  getDoc,
  setDoc
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { getBankAccounts } from "../../companies/models/company.js";
import { rowIdentities } from "../utils/statementPdf.js";

/**
 * Bank statement data (no PDF is ever stored — only the extracted transactions).
 * bankTransactions/{txnKey}: the key is a fingerprint of the transaction, so importing the same
 * transaction again (overlapping statements) always lands on the same document → no duplicates.
 * bankImports/{id}: one record per imported file, with the file's SHA-256 to detect re-uploads.
 */
const TXNS = "bankTransactions";
const IMPORTS = "bankImports";

const user = () => auth.currentUser?.email || auth.currentUser?.uid || "admin";
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

export async function sha256Hex(input) {
  const data = typeof input === "string" ? new TextEncoder().encode(input) : input;
  const hash = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Fingerprints for parsed rows: SHA-256 of account + row identity (see rowIdentities).
 * Used as the document id, so the same transaction imported again can never create a second record.
 */
export async function fingerprintRows(accountId, rows, hasBalance) {
  const ids = rowIdentities(rows, hasBalance);
  return Promise.all(ids.map((id) => sha256Hex(`${accountId}|${id}`)));
}

/**
 * Finds the company account a statement belongs to.
 * Auto-match only on the full account number, or the last digits plus the same IFSC.
 */
export function matchAccount(companies = [], found = {}) {
  const number = digitsOnly(found.number);
  const ifsc = String(found.ifsc || "").toUpperCase();
  const all = companies.flatMap((company) => getBankAccounts(company).map((account) => ({ company, account })));
  if (number) {
    const exact = all.filter(({ account }) => digitsOnly(account.accountNumber) === number);
    if (exact.length === 1) return { match: exact[0], all };
    if (ifsc && number.length >= 4) {
      const tail = number.slice(-4);
      const partial = all.filter(({ account }) => digitsOnly(account.accountNumber).endsWith(tail) && account.ifscCode === ifsc);
      if (partial.length === 1) return { match: partial[0], all };
    }
  }
  return { match: null, all };
}

export async function getImportByHash(fileHash) {
  const snap = await getDocs(query(collection(db, IMPORTS), where("fileHash", "==", fileHash)));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

/** Which fingerprints already exist (documentId "in" queries, 30 per query). */
export async function existingKeys(keys) {
  const found = new Set();
  for (let i = 0; i < keys.length; i += 30) {
    const chunk = keys.slice(i, i + 30);
    const snap = await getDocs(query(collection(db, TXNS), where(documentId(), "in", chunk)));
    snap.docs.forEach((d) => found.add(d.id));
  }
  return found;
}

/** Transactions for one account, optionally within a date range (YYYY-MM-DD), oldest first. */
export async function getAccountTransactions(accountId, { from = "", to = "" } = {}) {
  const constraints = [where("accountId", "==", accountId)];
  if (from) constraints.push(where("date", ">=", from));
  if (to) constraints.push(where("date", "<=", to));
  constraints.push(orderBy("date", "asc"));
  const snap = await getDocs(query(collection(db, TXNS), ...constraints));
  // Same-day order: time when the bank prints it, else the order in the statement
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => a.date.localeCompare(b.date) || String(a.time || "").localeCompare(String(b.time || "")) || (a.seq || 0) - (b.seq || 0));
}

export async function countAccountTransactions(accountId) {
  const snap = await getCountFromServer(query(collection(db, TXNS), where("accountId", "==", accountId)));
  return snap.data().count;
}

export async function getAccountImports(accountId) {
  const snap = await getDocs(query(collection(db, IMPORTS), where("accountId", "==", accountId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.periodTo || "").localeCompare(String(a.periodTo || "")));
}

/**
 * Same account + date + amount already saved under a different fingerprint
 * (e.g. the bank changed the narration/balance format between downloads).
 */
export async function findPossibleDuplicates(accountId, rows, keys, existing) {
  const dates = rows.map((r) => r.date).sort();
  if (!dates.length) return new Set();
  const saved = await getAccountTransactions(accountId, { from: dates[0], to: dates[dates.length - 1] });
  const savedSig = new Set(saved.map((t) => `${t.date}|${t.debitPaise}|${t.creditPaise}`));
  const flagged = new Set();
  rows.forEach((row, i) => {
    if (!existing.has(keys[i]) && savedSig.has(`${row.date}|${row.debitPaise}|${row.creditPaise}`)) flagged.add(i);
  });
  return flagged;
}

/** Writes the selected new rows plus the import record (batched, ≤ 450 writes per batch). */
export async function importTransactions({ company, account, parsed, rows, keys, fileName, fileHash, duplicates }) {
  const importRef = doc(collection(db, IMPORTS));
  const writes = rows.map((row, i) => ({
    ref: doc(db, TXNS, keys[i]),
    data: {
      companyId: company.id,
      accountId: account.id,
      date: row.date,
      description: row.description,
      ref: row.ref || "",
      time: row.time || "",
      seq: row.seq || 0,
      debitPaise: row.debitPaise,
      creditPaise: row.creditPaise,
      balancePaise: row.balancePaise,
      importId: importRef.id,
      createdAt: serverTimestamp()
    }
  }));
  writes.push({
    ref: importRef,
    data: {
      companyId: company.id,
      accountId: account.id,
      accountNumber: account.accountNumber,
      fileName,
      fileHash,
      periodFrom: parsed.period.from || rows[0]?.date || "",
      periodTo: parsed.period.to || rows[rows.length - 1]?.date || "",
      added: rows.length,
      duplicates,
      importedBy: user(),
      importedAt: serverTimestamp()
    }
  });
  for (let i = 0; i < writes.length; i += 450) {
    const batch = writeBatch(db);
    writes.slice(i, i + 450).forEach((w) => batch.set(w.ref, w.data));
    await batch.commit();
  }
  writeAudit("BANK_STATEMENT_IMPORTED", { companyId: company.id, accountId: account.id, fileName, added: rows.length, duplicates });
  return { importId: importRef.id, added: rows.length };
}

/** Undo an import: removes the transactions it added and its record. */
export async function deleteImport(imp) {
  const snap = await getDocs(query(collection(db, TXNS), where("importId", "==", imp.id)));
  const matched = snap.docs.filter((d) => d.data().matchStatus === "matched").length;
  if (matched) {
    throw new Error(`${matched} line${matched === 1 ? " is" : "s are"} matched to receipts/payments. Unmatch ${matched === 1 ? "it" : "them"} first (To match → Matched).`);
  }
  const refs = [...snap.docs.map((d) => d.ref), doc(db, IMPORTS, imp.id)];
  for (let i = 0; i < refs.length; i += 450) {
    const batch = writeBatch(db);
    refs.slice(i, i + 450).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  writeAudit("BANK_STATEMENT_IMPORT_UNDONE", { importId: imp.id, accountId: imp.accountId, fileName: imp.fileName, removed: snap.size, by: user() });
  return snap.size;
}

/* ---------- column layouts saved from the "map the columns" screen (per bank / IFSC prefix) ---------- */

const PROFILES_DOC = () => doc(db, "settings", "bankProfiles");

export async function getSavedBankProfile(bankCode) {
  const prefix = String(bankCode || "").slice(0, 4).toUpperCase();
  if (!prefix) return null;
  const snap = await getDoc(PROFILES_DOC());
  return snap.exists() ? snap.data()?.[prefix] || null : null;
}

export async function saveBankProfile(bankCode, profile) {
  const prefix = String(bankCode || "").slice(0, 4).toUpperCase();
  if (!prefix) return;
  await setDoc(PROFILES_DOC(), { [prefix]: { ...profile, savedBy: user(), savedAt: new Date().toISOString() } }, { merge: true });
  writeAudit("BANK_LAYOUT_SAVED", { prefix, columns: (profile.columns || []).map((c) => c.key).join(",") });
}
